import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useSport } from '@/hooks/useSport'
import { useTenant } from '@/contexts/TenantContext'
import { getBrandShortName } from '@/lib/tenant'
import { useApi } from '@/hooks/useApi'
import { usePolling } from '@/hooks/usePolling'
import { fetchRpiLatest, fetchPosts, fetchRecentScores, fetchNews, fetchCoachesPollLatest, fetchHighlights } from '@/lib/api'
import { DashboardSkeleton, Skeleton } from '@/components/ui/Skeleton'
import { RankBadge } from '@/components/ui/RankBadge'
import { TeamAvatar } from '@/components/ui/TeamAvatar'
import { ScoreCard } from '@/components/ScoreCard'
import { HighlightCard } from '@/components/HighlightCard'
import { SectionHeader } from '@/components/ui/SectionHeader'

type RankingRow = {
  rank: number
  school_name: string
  conference_name: string
  record: string
  prev_rank: number | null
  logo_url?: string | null
  slug: string
}

function nameToSlug(name: string) {
  return name.toLowerCase().replace(/\s+/g, '-')
}

// The curated overview page (rankings, movers, highlights, recent scores, news,
// posts). This used to be the homepage; the scoreboard is now the homepage and
// this lives at /:sport/dashboard.
export default function DashboardPage() {
  const { sport, sportLabel } = useSport()
  const tenant = useTenant()

  const rpi = useApi(() => fetchRpiLatest(sport), [sport])
  const poll = useApi(() => fetchCoachesPollLatest(sport), [sport])
  const posts = useApi(() => fetchPosts(sport).then(d => d.posts || []), [sport])
  const scores = useApi(() => fetchRecentScores(sport), [sport])
  const news = useApi(() => fetchNews(sport).then(d => d.news_articles || []), [sport])
  const highlights = useApi(() => fetchHighlights(sport).then(d => d.highlights || []), [sport])

  const scoresData = scores.data?.data || []
  const hasLiveGames = useMemo(() =>
    scoresData.some(g => {
      const s = g.status?.toLowerCase()
      return s === 'live' || s === 'in_progress'
    }), [scoresData])

  usePolling(() => scores.refetch(), 30_000, hasLiveGames)

  // RPI is the preferred ranking, but its source is often unavailable; fall
  // back to the coaches poll so the rankings are never empty.
  const { rankingRows, usingPoll } = useMemo(() => {
    const rpiRows = rpi.data?.rows || []
    if (rpiRows.length > 0) {
      return {
        usingPoll: false,
        rankingRows: rpiRows.map((r): RankingRow => ({
          rank: r.rank, school_name: r.school_name, conference_name: r.conference_name,
          record: r.record_overall, prev_rank: r.prev_rank, logo_url: r.logo_url,
          slug: r.slug || nameToSlug(r.school_name),
        })),
      }
    }
    const pollRows = poll.data?.rows || []
    return {
      usingPoll: true,
      rankingRows: pollRows.map((r): RankingRow => ({
        rank: r.rank, school_name: r.school_name, conference_name: r.conference_name,
        record: r.record, prev_rank: r.prev_rank, logo_url: r.logo_url,
        slug: r.team_slug || nameToSlug(r.school_name),
      })),
    }
  }, [rpi.data, poll.data])

  const rankingTitle = usingPoll ? 'Coaches Poll Top 25' : 'RPI Top 25'
  const rankingLink = usingPoll ? `/${sport}/coaches-poll` : `/${sport}/rpi`

  const movers = useMemo(() => rankingRows
    .filter((r) => r.prev_rank && r.prev_rank !== r.rank)
    .map((r) => ({ ...r, delta: (r.prev_rank ?? 0) - r.rank }))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 5), [rankingRows])

  const highlightsData = highlights.data || []

  if ((rpi.loading || poll.loading) && posts.loading) return <DashboardSkeleton />

  return (
    <div className="space-y-8">
      {/* Hero: Sport headline + biggest movers */}
      <section className="card-elevated p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center">
            <svg className="w-6 h-6 text-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 2a10 10 0 00-10 10M12 2a10 10 0 0110 10" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">{sportLabel} D1 {getBrandShortName(tenant.brandName)}</h1>
            <p className="text-sm text-slate-400">Rankings &amp; Analysis</p>
          </div>
        </div>

        {movers.length > 0 && (
          <>
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
              Biggest Movers This Week
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {movers.map((m) => (
                <Link
                  key={m.rank}
                  to={`/${sport}/teams/${m.slug}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-pitch-900/50 hover:bg-pitch-800/60 transition-colors"
                >
                  <TeamAvatar name={m.school_name} logoUrl={m.logo_url} colorSeed={m.conference_name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-white truncate">{m.school_name}</div>
                    <div className="text-xs text-slate-500">{m.record}</div>
                  </div>
                  <RankBadge rank={m.rank} prevRank={m.prev_rank} size="sm" />
                </Link>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Recent Scores ticker */}
      <section>
        <SectionHeader title="Recent Scores" linkTo={`/${sport}`} linkLabel="Scoreboard" />
        <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
          {scores.loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="snap-start shrink-0 w-72">
                <Skeleton variant="card" className="h-40" />
              </div>
            ))
          ) : (
            scoresData.slice(0, 8).map((game) => (
              <div key={game.DT_RowId} className="snap-start shrink-0 w-72">
                <ScoreCard
                  game={{
                    id: game.DT_RowId,
                    starts_at: game.starts_at,
                    status: game.status,
                    period: game.period,
                    clock: game.clock,
                    home_team_name: game.home_team_name,
                    away_team_name: game.away_team_name,
                    home_team_slug: game.home_team_slug,
                    away_team_slug: game.away_team_slug,
                    home_score: game.home_score,
                    away_score: game.away_score,
                    venue: game.venue,
                    home_team_conference: game.home_team_conference,
                    home_team_logo_url: game.home_team_logo_url,
                    away_team_logo_url: game.away_team_logo_url,
                    home_team_rank: game.home_team_rank,
                    away_team_rank: game.away_team_rank,
                  }}
                  sport={sport}
                />
              </div>
            ))
          )}
        </div>
      </section>

      {/* Video highlights */}
      {highlightsData.length > 0 && (
        <section>
          <SectionHeader title="Highlights" linkTo={`/${sport}/highlights`} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {highlightsData.slice(0, 3).map((h) => (
              <HighlightCard key={h.id} highlight={h} />
            ))}
          </div>
        </section>
      )}

      {/* Latest News horizontal scroll */}
      {(news.data || []).length > 0 && (
        <section>
          <SectionHeader title="Latest News" linkTo={`/${sport}/news`} />
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
            {(news.data || []).slice(0, 6).map((article) => (
              <div key={article.id} className="snap-start shrink-0 w-72">
                <Link
                  to={`/${sport}/news/${article.slug}`}
                  className="card group hover:border-accent/30 transition-colors overflow-hidden flex flex-col h-full"
                >
                  {article.image_url && (
                    <div className="aspect-video overflow-hidden bg-pitch-800">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-3 flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-accent">
                        {article.source}
                      </span>
                      {article.published_at && (
                        <span className="text-xs text-slate-500">
                          {new Date(article.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-medium text-slate-200 group-hover:text-white line-clamp-2">
                      {article.title}
                    </h3>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Two-column: Top 25 + Latest Posts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="card p-5">
          <SectionHeader title={rankingTitle} linkTo={rankingLink} linkLabel="Full Rankings" />
          <div className="space-y-0">
            {rankingRows.slice(0, 25).map((row) => (
              <Link
                key={row.rank}
                to={`/${sport}/teams/${row.slug}`}
                className="flex items-center gap-3 py-2 px-2 -mx-2 rounded-lg hover:bg-pitch-800/40 transition-colors group"
              >
                <div className="w-10">
                  <RankBadge rank={row.rank} prevRank={row.prev_rank} size="sm" />
                </div>
                <TeamAvatar name={row.school_name} logoUrl={row.logo_url} colorSeed={row.conference_name} size="sm" />
                <span className="flex-1 text-sm text-slate-200 group-hover:text-white truncate">
                  {row.school_name}
                </span>
                <span className="text-xs font-mono text-slate-500" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {row.record}
                </span>
              </Link>
            ))}
          </div>
          {(rpi.loading || poll.loading) && rankingRows.length === 0 && (
            <div className="space-y-1">
              {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} variant="row" />)}
            </div>
          )}
          {!rpi.loading && !poll.loading && rankingRows.length === 0 && (
            <p className="text-sm text-slate-500 py-3">Rankings are not available yet.</p>
          )}
        </section>

        <section className="card p-5">
          <SectionHeader title="Latest Posts" linkTo={`/${sport}/bracketology`} linkLabel="All Posts" />
          <div className="space-y-3">
            {(posts.data || []).slice(0, 8).map((post) => (
              <Link
                key={post.id}
                to={`/${sport}/bracketology/${post.slug}`}
                className="block p-3 -mx-3 rounded-lg hover:bg-pitch-800/40 transition-colors"
              >
                <div className="text-sm font-medium text-slate-200 hover:text-white line-clamp-2">{post.title}</div>
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="text-xs font-medium text-accent/80 uppercase tracking-wide">
                    {post.category?.replace('_', ' ')}
                  </span>
                  {post.published_at && (
                    <span className="text-xs text-slate-600">
                      {new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
          {posts.loading && <Skeleton variant="text" lines={8} />}
        </section>
      </div>
    </div>
  )
}
